import React from 'react'
import { Tabs, Tab, Box, Tooltip, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { INTERAX_COLORS } from '../constants/colors'

export interface GamerFeedbackTabBarProps {
  /**
   * Array of tab configurations
   */
  tabs: Array<{ label: string; value: string }>
  /**
   * Currently active tab value
   */
  activeTab: string
  /**
   * Tab change handler
   */
  onChange: (value: string) => void
  /**
   * Optional custom styling
   */
  sx?: SxProps<Theme>
  /** Optional disabled tab values */
  disabledValues?: string[]
  /** Label for disabled tabs */
  disabledLabel?: string
}

/**
 * GamerFeedbackTabBar - Custom tab bar component for feedback analysis
 *
 * Features:
 * - MUI Tabs with custom styling matching Frame 512640 CSS
 * - Active tab with glassmorphism effect
 * - Inactive tabs with transparent background
 * - Responsive width handling
 *
 * @example
 * ```tsx
 * <GamerFeedbackTabBar
 *   tabs={[
 *     { label: 'Plan', value: 'plan' },
 *     { label: 'Follow', value: 'follow' },
 *     { label: 'Adjust/Eval', value: 'adjust' }
 *   ]}
 *   activeTab="plan"
 *   onChange={(value) => setActiveTab(value)}
 * />
 * ```
 */
export const GamerFeedbackTabBar: React.FC<GamerFeedbackTabBarProps> = ({
  tabs,
  activeTab,
  onChange,
  sx,
  disabledValues = [],
  disabledLabel = '---'
}) => {
  const handleChange = (_event: React.SyntheticEvent, newValue: string | number) => {
    // MUI Tabs might pass number, but we always work with strings
    onChange(String(newValue))
  }

  // Don't render if no tabs
  if (!tabs || tabs.length === 0) {
    return null
  }

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        // Container styling - matches Frame 512640
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '4px',
        gap: '24px',
        width: '100%', // Full width

        background: INTERAX_COLORS.offBlack, // #212629
        boxShadow: '2px 2px 2px rgba(255, 255, 255, 0.1), inset 2px 2px 3px rgba(0, 0, 0, 0.5)',
        borderRadius: '10px',
        flex: 'none',
        order: 1,
        alignSelf: 'stretch',
        flexGrow: 0,
        ...sx
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleChange}
        variant="fullWidth"
        sx={{
          width: '100%',
          maxWidth: 'none',
          minHeight: '40px',
          '& .MuiTabs-flexContainer': {
            gap: '24px',
            padding: '0',
            width: '100%'
          },
          '& .MuiTabs-indicator': {
            display: 'none' // Hide default MUI indicator
          },
          '& .MuiTabs-scroller': {
            width: '100%'
          }
        }}
      >
        {tabs.map((tab) => {
          const tabValue = String(tab.value) // Ensure value is always a string - this is the unique identifier
          const isDisabled = disabledValues.includes(tabValue)
          const isActive = activeTab === tabValue
          const displayLabel = isDisabled ? disabledLabel : tab.label

          return (
            <Tab
              key={tabValue}
              label={
                <Tooltip title={displayLabel} arrow placement="top">
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'Lexend Deca',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: 'inherit',
                      textTransform: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      textAlign: 'center',
                      display: 'block'
                    }}
                  >
                    {displayLabel}
                  </Typography>
                </Tooltip>
              }
              value={tabValue}
              sx={{
                // Base tab styling
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 12px',
                gap: '10px',
                minWidth: 0,
                height: '40px',
                flex: 1,
                flexGrow: 1,
                borderRadius: '10px',
                // Active tab styling
                ...(isActive && {
                  background: INTERAX_COLORS.white10,
                  boxShadow: `0px 5px 10px rgba(0, 0, 0, 0.25), inset 0px -1px 1px ${INTERAX_COLORS.neutralBorder}`
                }),
                // Text styling - matches CSS
                fontFamily: 'Lexend Deca',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                color: INTERAX_COLORS.white,
                textTransform: 'none',
                // Override MUI default colors
                '&.Mui-selected': {
                  color: INTERAX_COLORS.white
                },
                '&:hover': {
                  ...(isActive || isDisabled
                    ? {}
                    : {
                        background: INTERAX_COLORS.white10
                      })
                }
              }}
              disabled={isDisabled}
            />
          )
        })}
      </Tabs>
    </Box>
  )
}

export default GamerFeedbackTabBar
